import { Box, TextField, Typography, IconButton } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { isValidEmail } from '../../helpers/strings';
import { Namespace } from '../../locales/translations';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import ActionButton from '../_include/Buttons/ActionButton';
import { AppRoutes } from '../../constants/routes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { TextInput } from '../../helpers/forms';
import UserMethods from '../../actions/user_action';
import { stringifyUrl } from 'query-string';

type ForgotPasswordPageProps = {};

type InputTypes = {
    email: TextInput;
};

function ForgotPasswordPage(props: ForgotPasswordPageProps) {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const emailParam = searchParams.get('email');
    const { t } = useTranslation([Namespace.ACTIONS, Namespace.FORMS, Namespace.TITLES]);
    const loading = useAppSelector((state) => state.user.loading);
    
    const [inputs, setInputs] = useState<InputTypes>({
        email: {
            value: emailParam ?? "",
            error: null,
        },
    });
    
    const { value: email, error: emailError } = inputs.email;
    
    const handleEmailChanged = (value: string) => {
        setInputs({
            ...inputs,
            email: {
                value,
                error: null,
            }
        });
    };

    const handleRequestResetLink = async () => {
        if (!isValidEmail(email)) {
            setInputs({
                ...inputs,
                email: {
                    ...inputs.email,
                    error: t("sign_in.invalid_email", { ns: Namespace.FORMS }),
                },
            });
            return;
        }

        dispatch(UserMethods.sendPasswordResetEmail(email));
    
    };

    const url = stringifyUrl({
        url: AppRoutes.LOGIN_PASSWORD,
        query: email ? { email } : {}
    });

    return (
        <Box
            display="flex"
            flexDirection="column"
            flexGrow={1}
            justifyContent="center"
            alignItems="center"
            px={2}
            maxWidth="500px"
            mx="auto"
        >
            <Box display="flex" alignItems="center" width="100%" mb={2}>
                <Link to={url} style={{ textDecoration: 'none' }}>
                    <IconButton sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                </Link>
                <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
                    {t("reset_password", { ns: Namespace.TITLES })}
                </Typography>
            </Box>
            
            <Typography variant="body1" mb={3} color="text.secondary" align="center">
                {t("reset_password_instructions", { ns: Namespace.FORMS })}
            </Typography>
            
            <Box width="100%" sx={{ mb: 2 }}>
                <TextField
                    placeholder={t("sign_in.email", { ns: Namespace.FORMS }) ?? ""}
                    value={email}
                    variant="outlined"
                    type="email"
                    onChange={(event) => handleEmailChanged(event.target.value)}
                    disabled={loading}
                    error={emailError !== null}
                    helperText={emailError}
                    fullWidth
                    sx={{
                        mb: 2,
                    }}
                />
                
                <ActionButton
                    color="primary"
                    disabled={!email}
                    loading={loading}
                    onClick={handleRequestResetLink}
                    fullWidth
                >
                    {t("send_reset_link", { ns: Namespace.ACTIONS })}
                </ActionButton>
            </Box>
        </Box>
    );
}

export default ForgotPasswordPage; 